// Accurate in-process timing for `scriptctl ingest`, persisted incrementally to
// workspace/timings.json so `scriptctl view --watch` and `scriptctl ingest status`
// show REAL wall-clock durations + a serial ETA (not mtime guesses). Written after
// each phase boundary and each serial window, so the live monitor ticks as the run
// progresses. Timing is per THIS run: a new run/resume starts a fresh record.

export interface WindowTiming { start: number; end: number; ms: number; cached: boolean }
export interface PhaseTiming { name: string; startedMs: number; endedMs: number | null }
export interface SerialTiming { totalWindows: number; totalEpisodes: number; windows: WindowTiming[] }
export interface IngestTimings {
  startedMs: number;
  endedMs: number | null;
  phases: PhaseTiming[];
  serial: SerialTiming | null;
}

// Records timing and flushes the whole record on every mutation. `flush` persists
// it (the pipeline writes workspace/timings.json); `now` is injectable for tests.
export class Timings {
  private t: IngestTimings;
  constructor(private readonly flush: (t: IngestTimings) => void, private readonly now: () => number = () => Date.now()) {
    this.t = { startedMs: this.now(), endedMs: null, phases: [], serial: null };
    this.flush(this.t);
  }
  phaseStart(name: string): void {
    this.t.phases.push({ name, startedMs: this.now(), endedMs: null });
    this.flush(this.t);
  }
  phaseEnd(name: string): void {
    for (let i = this.t.phases.length - 1; i >= 0; i--) {
      const p = this.t.phases[i]!;
      if (p.name === name && p.endedMs === null) { p.endedMs = this.now(); this.flush(this.t); return; }
    }
  }
  serialStart(totalWindows: number, totalEpisodes: number): void {
    this.t.serial = { totalWindows, totalEpisodes, windows: [] };
    this.flush(this.t);
  }
  windowDone(start: number, end: number, ms: number, cached: boolean): void {
    if (!this.t.serial) this.t.serial = { totalWindows: 0, totalEpisodes: 0, windows: [] };
    this.t.serial.windows.push({ start, end, ms, cached });
    this.flush(this.t);
  }
  finish(): void { this.t.endedMs = this.now(); this.flush(this.t); }
}

// A now-relative presentation summary for `ingest status --json` consumers.
export interface TimingSummary {
  elapsedMs: number;
  done: boolean;
  phases: Array<{ name: string; ms: number; running: boolean }>;
  serial: {
    doneWindows: number;
    totalWindows: number;
    doneEpisodes: number;
    totalEpisodes: number;
    avgWindowMs: number | null; // over REAL (non-cached) windows only
    etaMs: number | null;
  } | null;
}

// Pure: compute elapsed/phase/eta at a given `nowMs`. ETA extrapolates the average
// of REAL (non-cached) serial windows over the windows still to run; cached windows
// (resume replays) are excluded so a resumed run's ETA isn't skewed toward zero.
export function summarizeTimings(t: IngestTimings, nowMs: number): TimingSummary {
  const elapsedMs = (t.endedMs ?? nowMs) - t.startedMs;
  const phases = t.phases.map((p) => ({ name: p.name, ms: (p.endedMs ?? nowMs) - p.startedMs, running: p.endedMs === null }));
  let serial: TimingSummary["serial"] = null;
  if (t.serial) {
    const w = t.serial.windows;
    const real = w.filter((x) => !x.cached);
    const avg = real.length ? real.reduce((a, x) => a + x.ms, 0) / real.length : null;
    const doneEpisodes = w.reduce((a, x) => a + (x.end - x.start + 1), 0);
    const remainingWindows = Math.max(0, t.serial.totalWindows - w.length);
    serial = {
      doneWindows: w.length,
      totalWindows: t.serial.totalWindows,
      doneEpisodes,
      totalEpisodes: t.serial.totalEpisodes,
      avgWindowMs: avg != null ? Math.round(avg) : null,
      etaMs: avg != null ? Math.round(avg * remainingWindows) : null,
    };
  }
  return { elapsedMs, done: t.endedMs !== null, phases, serial };
}
